import i18n from "i18next";

import { LinkIcon } from "@/assets/icons";
import { InfoItemProps } from "@/components/organisms";
import { defaultDateFormat } from "@/core/date";
import { defaultCurrencyFormat, defaultPercentFormat } from "@/core/number";
import { getLink } from "@/shared/utils";

export const panelCircumstancesSchema = (circumstances: MyBrokerApi.Circumstances): InfoItemProps[] => {
    return [
        {
            key: "liability",
            label: i18n.t("Common.Responsibility"),
            value: circumstances.liability?.name,
        },
        {
            key: "circ1",
            label: i18n.t("Common.CircX", { number: 1 }),
            value: circumstances.circ1?.name,
        },
        {
            key: "circ2",
            label: i18n.t("Common.CircX", { number: 2 }),
            value: circumstances.circ2?.name,
        },
        {
            key: "circ3",
            label: i18n.t("Common.CircX", { number: 3 }),
            value: circumstances.circ3?.name,
        },
        {
            key: "circ4",
            label: i18n.t("Common.CircX", { number: 4 }),
            value: circumstances.circ4?.name,
        },
    ];
};

export const panelStakeholdersSchema = (
    onSecureUrlClick: (linkUrl: string) => void,
    stakeholders: MyBrokerApi.Stakeholder[] = [],
): InfoItemProps[] =>
    stakeholders.map((stakeholder) => {
        const stakeholderName = stakeholder.reference
            ? `${stakeholder.name} ${stakeholder.reference}`
            : stakeholder.name;
        const insurerLink = getLink(stakeholder, "partner")?.href;
        const withInsurerLink = !!insurerLink;
        return {
            key: `stakeholder${stakeholder.quality?.id}`,
            label: stakeholder.quality?.name,
            isLink: withInsurerLink,
            value: withInsurerLink ? `<span class="link">${stakeholder.name}</span>` : stakeholderName,
            IconComponent: withInsurerLink ? LinkIcon : null,
            onClick:
                withInsurerLink &&
                ((e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSecureUrlClick(insurerLink);
                }),
        };
    });

export const panelConcernedsSchema = (
    concerned: MyBrokerApi.ObjectConcerned,
    index: number,
    language: string,
): InfoItemProps[] => {
    const schema = [
        {
            key: `concerned${index}`,
            label: concerned.insuredGoodType.name,
            value: concerned.name,
        },
    ] as InfoItemProps[];

    schema.pushIf(concerned.warranties.length > 0, {
        key: `warranties${index}`,
        label: i18n.t("Common.WarrantiesApplied"),
        features: concerned.warranties.map((warranty) => {
            return {
                key: `feature${warranty.coverage.id}`,
                label: warranty.coverage.name,
                infos: [
                    {
                        key: `deductible${warranty.coverage.id}`,
                        label: i18n.t("Common.Franchise"),
                        value: defaultCurrencyFormat(warranty.deductible, language),
                        hideIfZero: true,
                    },
                    {
                        key: `deductibleRate${warranty.coverage.id}`,
                        label: i18n.t("Common.Franchise"),
                        value: defaultPercentFormat(warranty.deductibleRate, language),
                        hideIfZero: true,
                    },
                ],
            };
        }),
    });

    return schema;
};

export const panelSettlementSchema = (
    settlements: MyBrokerApi.Settlement[],
    index: number,
    language: string,
): InfoItemProps[] => {
    const schema = [] as InfoItemProps[];

    schema.pushIf(settlements.length > 0, {
        key: `settlements${index}`,
        payments: settlements.map((settlement, i) => ({
            key: `settlement${i}`,
            index: i,
            amount: defaultCurrencyFormat(settlement.amount, language, false),
            date: settlement.date,
            bankAccount: settlement.bankAccount,
            beneficiary: settlement.beneficiary,
        })),
    });

    return schema;
};

export const panelExpertiseSchema = (expertise: MyBrokerApi.Expertise, language: string): InfoItemProps[] => [
    {
        key: "sentDate",
        label: i18n.t("Common.SentDate"),
        value: defaultDateFormat(expertise.sentDate),
    },
    {
        key: "expertiseDate",
        label: i18n.t("Common.ExpertiseDate"),
        value: defaultDateFormat(expertise.expertiseDate),
    },
    {
        key: "receptionDate",
        label: i18n.t("Common.ReceiptDateOfReport"),
        value: defaultDateFormat(expertise.receptionDate),
    },
    {
        key: "amountHTVA",
        label: i18n.t("Common.AmountTVA"),
        value: defaultCurrencyFormat(expertise.amountHTVA, language, false),
    },
];

export const panelManagerSchema = (manager: MyBrokerApi.Manager): InfoItemProps[] => [
    {
        key: "name",
        label: i18n.t("Common.Name"),
        value: manager.name,
    },
    {
        key: "phone",
        label: i18n.t("Common.Phone"),
        value: manager.phone,
    },
    {
        key: "email",
        label: i18n.t("Common.Email"),
        value: manager.email,
    },
];
